"use client";

import {
  Bell,
  Calendar,
  ExternalLink,
  ExternalLinkIcon,
  FolderClosed,
  HelpCircle,
  Home,
  Info,
  LogOut,
  MapPin,
  Phone,
  Users,
  Utensils,
} from "lucide-react";
import type { ComputedNavItem, VenuePageUrlBase } from "./utils";
import { CollapsibleNavGroup } from "@punchup/ui-common/components/common";
import { Database, Tables } from "@punchup/data/supabase.types";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@punchup/ui-common/shadcn/ui/popover";
import { ReactElement, useMemo, useState } from "react";
import {
  UserEvents,
  VenuePageEvents,
  resetAnalytics,
  trackEvent,
} from "@punchup/analytics";
import { buildNavTree, computeNavItems } from "./utils";
import {
  headerHamburgerButtonStyles,
  headerLinkStyles,
} from "@punchup/ui-common/components/header/styles";
import { usePathname, useRouter } from "next/navigation";
import {
  useVenuePageCustomLinks,
  useVenuePageExternalLinks,
  useVenuePageFAQs,
  useVenuePageNavGroups,
  useVenuePageNavItems,
  useVenuePageTheme,
} from "@punchup/venues";

import { BASE_NAV_ITEM_KEYS } from "../../../data/navigation";
import { HamburgerMenuIcon } from "@punchup/ui-common/components/common/icons";
import { default as HeaderNavigationMenuItem } from "@punchup/ui-common/components/header/HeaderNavigationMenuItem";
import VenuePageDrawerMenu from "../VenuePageDrawerMenu";
import { cn } from "@punchup/utils/cn";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { getWebAppHostname } from "@punchup/utils/hostname";
import { useMediaQuery } from "@punchup/utils/hooks";
import { useSession } from "@punchup/data/legacy";

const BASE_ITEM_ICONS: Record<string, React.ReactNode> = {
  [BASE_NAV_ITEM_KEYS.HOME]: <Home className="w-5 h-5" />,
  [BASE_NAV_ITEM_KEYS.CALENDAR]: <Calendar className="w-5 h-5" />,
  [BASE_NAV_ITEM_KEYS.MAP_PARKING]: <MapPin className="w-5 h-5" />,
  [BASE_NAV_ITEM_KEYS.MENU]: <Utensils className="w-5 h-5" />,
  [BASE_NAV_ITEM_KEYS.FAQS]: <HelpCircle className="w-5 h-5" />,
  [BASE_NAV_ITEM_KEYS.CONTACT]: <Phone className="w-5 h-5" />,
  [BASE_NAV_ITEM_KEYS.ABOUT]: <Info className="w-5 h-5" />,
  [BASE_NAV_ITEM_KEYS.SUBSCRIBE]: <Bell className="w-5 h-5" />,
  [BASE_NAV_ITEM_KEYS.SOCIALS]: <Users className="w-5 h-5" />,
};

function getIconForNavItem(item: ComputedNavItem): React.ReactNode {
  if (item.itemType === "base") {
    return BASE_ITEM_ICONS[item.itemKey] ?? <Info className="w-5 h-5" />;
  }
  if (item.itemType === "external") {
    return <ExternalLinkIcon className="w-5 h-5" />;
  }
  if (item.itemType === "group") {
    return <FolderClosed className="w-5 h-5" />;
  }
  return <Info className="w-5 h-5" />;
}

interface Props {
  onClick: () => void;
  venuePage: Tables<"venue_pages">;
  urlBase?: VenuePageUrlBase;
}

export default function VenuePageHamburgerMenu({
  onClick,
  venuePage,
  urlBase = "/v/",
}: Props) {
  const { slug, id: venueId } = venuePage;
  const { headerTextColor } = useVenuePageTheme(slug);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const session = useSession();

  const { externalLinks } = useVenuePageExternalLinks(venueId);
  const { customLinks } = useVenuePageCustomLinks(venueId);
  const { navItems: navItemOverrides } = useVenuePageNavItems(venueId);
  const { navGroups } = useVenuePageNavGroups(venueId);
  const { faqs } = useVenuePageFAQs(venueId);
  const supabase = createClientComponentClient<Database>();

  const hasFAQs = faqs?.length > 0;
  const hasMenu = !!venuePage.food_menu || !!venuePage.drinks_menu;
  const isLoggedIn = session !== undefined && session !== null;

  const navItems = useMemo(() => {
    return computeNavItems({
      venuePage,
      customLinks,
      externalLinks,
      navItems: navItemOverrides,
      navGroups,
      hasFAQs,
      hasMenu,
      urlBase,
    });
  }, [
    venuePage,
    customLinks,
    externalLinks,
    navItemOverrides,
    navGroups,
    hasFAQs,
    hasMenu,
    urlBase,
  ]);

  const navTree = useMemo(() => buildNavTree(navItems), [navItems]);

  const trackItemClick = (item: ComputedNavItem) =>
    trackEvent(VenuePageEvents.HEADER_HAMBURGER_MENU_ITEM_CLICKED, {
      label: item.label,
      href: item.href,
      itemType: item.itemType,
    });

  const popoverContent = (
    <ul>
      {navTree.map((node) => {
        if (node.itemType === "group") {
          return (
            <li key={`group-${node.itemKey}`} className="block">
              <CollapsibleNavGroup
                label={node.label}
                icon={<FolderClosed className="w-5 h-5" />}
                triggerClassName="flex items-center justify-start w-full px-4 text-gray-800 transition-colors duration-150 ease-in-out hover:bg-gray-50"
                triggerInnerClassName="py-2"
                chevronClassName="w-4 h-4"
              >
                <ul>
                  {(node.children ?? []).map((child) => (
                    <HeaderNavigationMenuItem
                      key={`${child.itemType}-${child.itemKey}`}
                      onClick={() => trackItemClick(child)}
                      href={child.href}
                      linkAriaLabel={child.label}
                      icon={getIconForNavItem(child) as ReactElement}
                      target={child.type === "external" ? "_blank" : undefined}
                    >
                      {child.label}
                    </HeaderNavigationMenuItem>
                  ))}
                </ul>
              </CollapsibleNavGroup>
            </li>
          );
        }
        return (
          <HeaderNavigationMenuItem
            key={`${node.itemType}-${node.itemKey}`}
            onClick={() => trackItemClick(node)}
            href={node.href}
            linkAriaLabel={node.label}
            icon={getIconForNavItem(node) as ReactElement}
            target={node.type === "external" ? "_blank" : undefined}
          >
            {node.label}
          </HeaderNavigationMenuItem>
        );
      })}

      {/* Separator and Punchup link */}
      <div className="h-px my-1 bg-neutral-200" />
      {isLoggedIn && (
        <HeaderNavigationMenuItem
          onClick={async () => {
            trackEvent(VenuePageEvents.HEADER_HAMBURGER_MENU_ITEM_CLICKED, {
              label: "Sign Out",
            });
            const { error } = await supabase.auth.signOut({ scope: "local" });
            if (error) {
              console.error("Error signing out:", error);
            } else {
              trackEvent(UserEvents.LOGGED_OUT);
              resetAnalytics();
              router.push("/?loggedOut");
            }
          }}
          linkAriaLabel="Sign Out"
          icon={<LogOut className="w-5 h-5" />}
        >
          Sign Out
        </HeaderNavigationMenuItem>
      )}

      <HeaderNavigationMenuItem
        onClick={() =>
          trackEvent(VenuePageEvents.HEADER_HAMBURGER_MENU_ITEM_CLICKED, {
            label: "Go to Punchup",
          })
        }
        href={getWebAppHostname()}
        linkAriaLabel="Go to Punchup"
        icon={<ExternalLink className="w-5 h-5" />}
      >
        Go to Punchup
      </HeaderNavigationMenuItem>
    </ul>
  );

  const triggerButton = (
    <button
      type="button"
      aria-label="Hamburger Menu"
      className={cn(headerLinkStyles, headerHamburgerButtonStyles)}
      style={{
        stroke: headerTextColor,
      }}
      onClick={() => {
        onClick();
        if (!isDesktop) {
          setIsDrawerOpen(true);
        }
      }}
    >
      <HamburgerMenuIcon className="w-[28px] h-[28px] stroke-inherit" />
    </button>
  );

  if (isDesktop) {
    return (
      <Popover>
        <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
        <PopoverContent
          className="p-0 w-52 overflow-clip"
          align="end"
          sideOffset={6}
        >
          {popoverContent}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <>
      {triggerButton}
      <VenuePageDrawerMenu
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        navItems={navItems}
        pathname={pathname}
        venueSlug={slug}
      />
    </>
  );
}
